import type { EAssistantSharePermission } from "./assistant-share";
import type {
  IMessage,
  IPresignFileUploadParams,
  IPresignFileUploadResponse,
} from "./chat";
import type { EIntegrationProvider } from "./integration";
import type {
  EModelHostingProvider,
  EModelProvider,
  EModelRegion,
} from "./models";

export enum EAssistantType {
  ALL = "all",
  RECOMMENDED = "recommended",
  OWNED = "owned",
  SHARED = "shared",
}

// NOTE: NSAI-3830 — visibility scope of a marketplace listing. ORGANIZATION
// makes the assistant usable in every project of the organization;
// SELECTED_PROJECTS only in the explicitly targeted ones.
export enum EAssistantMarketplaceScope {
  ORGANIZATION = "organization",
  SELECTED_PROJECTS = "selected_projects",
}

// NOTE: Marketplace block attached to assistant responses. Non-null only when
// the assistant is surfaced via a marketplace listing (marketplace browse, or
// a default listing merged into the regular list).
export interface IAssistantMarketplaceInfo {
  listingUuid: string;
  accessScope: EAssistantMarketplaceScope;
  /** Default (standard) assistants are auto-enabled for every user in the organization. */
  isDefault: boolean;
  publishedAt: string;
  publishedByFirstName: string | null;
  publishedByLastName: string | null;
  sourceProjectUuid: string | null;
  sourceProjectTitle: string | null;
  targetProjectUuids: string[];
}

// NOTE: File metadata returned by the API — no base64 (too expensive to include in response)
export interface IAssistantFile {
  uuid: string;
  name: string;
  type: string;
}

// NOTE: Reference to an existing file the user wants to keep during update
export interface IExistingFileRef {
  uuid: string;
}

// NOTE: New file input after the client has uploaded it to S3 via a presigned URL
// (POST /assistants/files/presign). Only the resulting s3Key is sent in the
// create/update payload — the server fetches the buffer from S3 for parsing/RAG.
export interface INewFileInput {
  name: string;
  type: string;
  s3Key: string;
}

export type TAssistantFilePayload = IExistingFileRef | INewFileInput;

export interface IAssistant {
  uuid: string;
  avatar: string | null;
  name: string;
  isPublic: boolean;
  description: string | null;
  instructions: string | null;
  files: Array<IAssistantFile> | null;
  integrations: EIntegrationProvider[];
  customIntegrations: string[];
  modelUuid: string;
  modelTitle: string | null;
  modelName: string;
  modelProvider: EModelProvider;
  modelHostingProvider: EModelHostingProvider;
  modelRegion: EModelRegion;
  modelSpeed: number;
  modelQuality: number;
  modelContextSize: number;
  isDocsSupported: boolean;
  isImagesSupported: boolean;
  isCanvasSupported: boolean;
  isWebSearchEnabled: boolean;
  isToolSupported: boolean;
  isImageGenerationModel: boolean;
  isVideoGenerationModel?: boolean;
  isReasoningModel: boolean;
  ownerFirstName?: string | null;
  ownerLastName?: string | null;
  messageCount?: number;
  /** true when the assistant has no real owner (ownerId IS NULL) — i.e. a system / app / recommended assistant. The UI hides edit/delete/share for these. */
  isAppAssistant: boolean;
  /** true/false when a viewer is in scope (user-facing endpoints); null when produced without a viewer (internal flows). Always present on list + single-get responses. */
  isOwnedByYou: boolean | null;
  /** Non-null only when the assistant is shared with the current user. Always present on list + single-get responses. */
  sharedBy: IAssistantSharedBy | null;
  /** Non-null only when the assistant is surfaced via a marketplace listing. */
  marketplace: IAssistantMarketplaceInfo | null;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
}

// NOTE: NSAI-6814 — query for GET /assistants/paginated. `search` is matched
// server-side across every assistant in scope (name, description, owner, sharer).
export interface IGetAssistantsPageParams {
  type?: EAssistantType;
  page?: number;
  limit?: number;
  search?: string;
}

// NOTE: NSAI-6814 — one page of the Assistants list. `data` already carries the
// `sharedBy` block each card renders, so the list never needs a per-assistant
// GET /assistants/:uuid/shares — that is read only when the share dialog opens.
export interface IAssistantsPage {
  data: IAssistant[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

// NOTE: Marketplace browse item — same shape as IAssistant with a guaranteed
// marketplace block.
export interface IMarketplaceAssistant extends IAssistant {
  marketplace: IAssistantMarketplaceInfo;
}

export interface IPublishAssistantPayload {
  accessScope?: EAssistantMarketplaceScope;
  isDefault?: boolean;
  targetProjectUuids?: string[];
}

export interface IPublishAssistantParams {
  assistantUuid: string;
  data: IPublishAssistantPayload;
}

export interface IAssistantSharedBy {
  permission: EAssistantSharePermission;
  /** UUID of the share record — used by the "Leave" action and by the share-management UI. */
  uuid: string;
  firstName: string | null;
  lastName: string | null;
}

// NOTE: `isDocsSupported`, `isImagesSupported` and `isPublic` are REQUIRED, not
// optional. The server's `CreateOrUpdateAssistantDto` marks each `@IsBoolean()`
// with no `@IsOptional()`, and the same DTO guards `POST /assistants`,
// `POST /assistants/temporary` and `PUT /assistants/:uuid` — so an omitted flag
// is a 400 the user meets as a raw validation string ("Public must be a valid
// boolean", NSAI-4691). Declaring them required is what makes the compiler catch
// the omission at the call site instead of the server catching it in front of
// the user.
export interface ICreateOrUpdateAssistantPayload {
  name: string;
  modelUuid: string;
  isDocsSupported: boolean;
  isImagesSupported: boolean;
  isPublic: boolean;
  description?: string | null;
  instructions?: string | null;
  avatar?: string | null;
  files?: Array<TAssistantFilePayload> | null;
  integrations?: EIntegrationProvider[];
  customIntegrations?: string[];
}

export interface IUpdateAssistantParams {
  uuid: string;
  data: ICreateOrUpdateAssistantPayload;
}

// NOTE: Parameters for releasing uncommitted presigned uploads. Client sends
// every s3Key produced during the form session; the server deletes only the
// uncommitted ones.
export interface IReleaseFileUploadsParams {
  s3Keys: string[];
}

export interface IReleaseFileUploadsResult {
  releasedCount: number;
}

// NOTE: Shape of the platform-agnostic tracker shared between web + mobile.
// Web wraps it with beforeunload/pagehide/unmount side-effects; mobile uses it
// directly via useRef (there are no browser-level unload events to hook on RN).
export interface IAssistantUploadTracker {
  // NOTE: Wraps `assistantsApi.presignFileUpload` and records the returned
  // s3Key in the tracker BEFORE the caller begins the PUT — so even a partial
  // or aborted PUT leaves its key in the cleanup list.
  presignAndTrack: (
    params: IPresignFileUploadParams,
  ) => Promise<IPresignFileUploadResponse>;
  // NOTE: Flip to "committed" BEFORE the save POST starts so unload handlers
  // don't issue a racing release call that could delete S3 objects while the
  // save transaction is still writing the DB rows that reference them.
  markCommitted: () => void;
  // NOTE: Revert to "uncommitted" if the save throws — the tracked keys are
  // still genuinely orphaned and need to be cleaned up on the next exit.
  resetCommitted: () => void;
  // NOTE: True when there are tracked s3Keys AND the session has not yet been
  // marked committed. Drives S3-cleanup paths only (pagehide beacon, unmount
  // release). Does NOT drive the beforeunload prompt — see `hasUnsavedFileChanges`.
  hasPendingRelease: () => boolean;
  // NOTE: True when there are ANY unsaved file-side changes — new uploads
  // awaiting release OR existing-file removals awaiting the server's
  // differential sync on submit. Drives the beforeunload "Leave site?"
  // prompt so refreshing with a queued existing-file removal still warns
  // the user even though no S3 cleanup is pending locally.
  hasUnsavedFileChanges: () => boolean;
  // NOTE: Queue an existing (DB-committed) file for removal. No S3 delete
  // fires here — the server handles DB + S3 + RAG cleanup inside
  // `syncAssistantFiles` on submit. Tracking it locally only so the
  // beforeunload prompt knows an unsaved change exists.
  trackExistingFileRemoval: (fileUuid: string) => void;
  // NOTE: Delete ONE s3Key from S3 and untrack it immediately so
  // `hasPendingRelease()` flips right away — prevents the beforeunload prompt
  // from firing if the user refreshes between the X click and the server
  // response. Used by:
  //   - the X button on an uploaded-but-not-yet-submitted attachment
  //   - the Cancel button on an in-flight upload (after XHR abort)
  // Server errors degrade silently; the 24h bucket lifecycle rule is the
  // backstop for leaked objects.
  releaseAndUntrack: (s3Key: string) => Promise<void>;
  // NOTE: Await-able release for explicit exit actions (Cancel, Back, submit
  // success). Retries once on transient failure; the server filters committed
  // keys so it is always safe to call, including after successful submit.
  releaseAll: () => Promise<void>;
  // NOTE: Fire-and-forget variant for browser unload (pagehide). Marks the
  // tracker committed on fire so a follow-up unmount-effect release is not
  // duplicated against the same keys.
  releaseAllBeacon: () => void;
}

export interface IModelInfo {
  label: string;
  providerLabel: string;
  icon: (props: { className?: string }) => React.ReactNode;
  isWebSearchEnabled: boolean;
  isImagesSupported: boolean;
  isDocsSupported: boolean;
  isToolSupported: boolean;
  contextSize: number;
  quality: number;
  speed: number;
  region: EModelRegion;
  hostingProvider: EModelHostingProvider;
  // NOTE: NSAI-4784 — short blurb shown in the info card/panel, mirroring
  // `IBaseModelFields.tagline` (models.ts). Optional/null since not every
  // model has one, and assistants (also rendered through this shape) don't
  // carry a tagline at all.
  tagline?: string | null;
  isImageGenerationModel?: boolean;
  isVideoGenerationModel?: boolean;
  assistant?: Pick<IAssistant, "name" | "description" | "avatar">;
}

// NOTE: Minimal attachment shape the `createOptimisticPreviewMessages`
// helper needs. Kept narrow on purpose so frontend-shared doesn't have to
// know about web's `IAttachment` or mobile's equivalent — callers pass
// whatever subset of fields they have. Mobile preview currently has no
// attachments in chat input, so it omits this field entirely.
export interface IOptimisticPreviewAttachmentInput {
  name: string;
  type: string;
}

// NOTE: Input shape for the optimistic-UI helper used by the create-
// assistant preview chat. The helper seeds a (user, assistant) message
// pair the instant the user sends, before the WS round-trip delivers the
// real pair. `attachments` is optional because mobile's preview chat
// doesn't support file uploads — it just sends text.
export interface ICreateOptimisticPreviewMessagesProps {
  content: string;
  attachments?: IOptimisticPreviewAttachmentInput[];
  chatUuid: string;
  modelUuid: string;
  modelTitle: string;
  provider: string;
}

// NOTE: Return shape for the optimistic-UI helper. Both messages carry
// TEMP_MESSAGE_PREFIX uuids so `upsertNewMessagesIntoChatHistory` (and the
// mobile equivalent) strip them automatically when the real WS-delivered
// pair lands in the cache.
export interface IOptimisticPreviewMessages {
  userMessage: IMessage;
  assistantMessage: IMessage;
}
